<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['delete', 'edit']);

const props = defineProps({
  application: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const showDeleteModal = ref(false);
const showSecret = ref(false);

const scopesArray = computed(() => {
  if (Array.isArray(props.application.scopes)) {
    return props.application.scopes;
  }
  if (typeof props.application.scopes === 'string') {
    return props.application.scopes.split(' ').filter(Boolean);
  }
  return [];
});

const copyToClipboard = async value => {
  await copyTextToClipboard(value);
  useAlert(t('OAUTH_APPLICATIONS.ITEM.COPY_SUCCESS'));
};

const confirmDelete = () => {
  emit('delete', props.application.id);
  showDeleteModal.value = false;
};

const formattedDate = date => {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-1"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="text-base font-medium text-n-slate-12 truncate">
          {{ application.name }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.ITEM.CREATED') }}
          {{ formattedDate(application.created_at) }}
        </p>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <Button
          v-tooltip.top="$t('OAUTH_APPLICATIONS.ITEM.EDIT_TOOLTIP')"
          icon="i-lucide-pen"
          slate
          xs
          faded
          @click="emit('edit', application)"
        />
        <Button
          v-tooltip.top="$t('OAUTH_APPLICATIONS.DELETE.CONFIRM.TITLE')"
          icon="i-lucide-trash-2"
          xs
          ruby
          faded
          @click="showDeleteModal = true"
        />
      </div>
    </div>

    <div class="grid gap-3">
      <div>
        <p class="mb-1 text-xs font-medium text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.ITEM.CLIENT_ID') }}
        </p>
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-md bg-n-alpha-2"
        >
          <code class="flex-1 text-sm font-mono text-n-slate-12 break-all">
            {{ application.uid }}
          </code>
          <Button
            v-tooltip.top="$t('OAUTH_APPLICATIONS.ITEM.COPY_CLIENT_ID')"
            icon="i-lucide-copy"
            slate
            xs
            ghost
            @click="copyToClipboard(application.uid)"
          />
        </div>
      </div>

      <div>
        <p class="mb-1 text-xs font-medium text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.ITEM.CLIENT_SECRET') }}
        </p>
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-md bg-n-alpha-2"
        >
          <code class="flex-1 text-sm font-mono text-n-slate-12 break-all">
            {{
              showSecret
                ? application.secret
                : '••••••••••••••••••••••••••••'
            }}
          </code>
          <Button
            v-tooltip.top="
              showSecret
                ? $t('OAUTH_APPLICATIONS.ITEM.HIDE_SECRET')
                : $t('OAUTH_APPLICATIONS.ITEM.SHOW_SECRET')
            "
            :icon="showSecret ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            slate
            xs
            ghost
            @click="showSecret = !showSecret"
          />
          <Button
            v-tooltip.top="$t('OAUTH_APPLICATIONS.ITEM.COPY_CLIENT_SECRET')"
            icon="i-lucide-copy"
            slate
            xs
            ghost
            @click="copyToClipboard(application.secret)"
          />
        </div>
      </div>

      <div>
        <p class="mb-1 text-xs font-medium text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.ITEM.SCOPES') }}
        </p>
        <div class="flex flex-wrap gap-1.5">
          <span
            v-for="scope in scopesArray"
            :key="scope"
            class="px-2 py-0.5 text-xs font-medium rounded bg-n-brand/10 text-n-brand"
          >
            {{ scope }}
          </span>
        </div>
      </div>

      <div>
        <p class="mb-1 text-xs font-medium text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.ITEM.REDIRECT_URI') }}
        </p>
        <div class="px-3 py-2 rounded-md bg-n-alpha-2">
          <p class="text-sm font-mono text-n-slate-12 break-all">
            {{ application.redirect_uri }}
          </p>
        </div>
      </div>
    </div>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="$t('OAUTH_APPLICATIONS.DELETE.CONFIRM.TITLE')"
      :message="$t('OAUTH_APPLICATIONS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('OAUTH_APPLICATIONS.DELETE.CONFIRM.YES')"
      :reject-text="$t('OAUTH_APPLICATIONS.DELETE.CONFIRM.NO')"
    />
  </div>
</template>
